import React, { useState } from 'react';
import AppDialog from '../../Dialogs/AppDialog/AppDialog';
import { Button } from 'primereact/button';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import './IconPicker.scss';
import { useTranslation } from 'react-i18next';


interface IconPickerProps {
  /** current value, e.g. "pi pi-home" */
  value?: string;
  /** RHF/InputField‑style change handler */
  onChange: (e: { value: string; target: { name?: string; value: string } } | string) => void;
  /** full icon list coming from IconService */
  icons: { name: string; tags: string[] }[];
  /** forwarded name so we can echo it back in the event */
  name?: string;
  /** allow host to pass extra props (className, id, …) */
  [key: string]: any;
}

const IconPicker: React.FC<IconPickerProps> = ({ value, onChange, icons, name = 'icon', className, ...rest }) => {
  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<typeof icons>([]);
  const { t } = useTranslation();

  const sanitize = (input: unknown) =>
    typeof input === 'string'
      ? input.replace(/[^\w\s]/gi, '').replace(/\s/g, '').toLowerCase()
      : '';

  const search = (e: AutoCompleteCompleteEvent) => {
    const q = sanitize(e.query);
    if (!q.length) {
      setSuggestions(icons.slice(0, 40));
      return;
    }

    setSuggestions(
      icons
        .filter(
          (icon) =>
            sanitize(icon.name).includes(q) || icon.tags?.some((t) => sanitize(t).includes(q))
        )
        .slice(0, 40)
    );
  };

  const mkEvent = (iconClass: string) => ({ value: iconClass, target: { name, value: iconClass } });

  const commit = (iconClass: string) => {
    // fire a RHF‑friendly event object
    onChange(mkEvent(iconClass));
    setVisible(false);
  };

  const itemTemplate = (item: { name: string }) => (
    <div className="icon-picker-suggestion">
      <i className={`pi pi-${item.name} mr-2`} />
      <span>{item.name}</span>
    </div>
  );

  const filteredIcons = icons.filter(
    (icon) =>
      sanitize(icon.name).includes(sanitize(query)) ||
      icon.tags?.some((t) => sanitize(t).includes(sanitize(query)))
  );

  return (
    <>
      <Button
        type="button"
        text
        icon={value ? value : undefined}
        label={value ? '' : t('pick.icon')}
        onClick={() => setVisible(true)}
        className={['icon-picker-trigger', value ? 'icon-picker-trigger--selected' : '', className]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      />

      <AppDialog
        header={t('icon.picker.title')}
        visible={visible}
        onHide={() => setVisible(false)}
        className="icon-picker-dialog"
      >
        <div className="icon-picker-toolbar">
          <AutoComplete
            value={query}
            onChange={(e) => {
              const val = e.value as any;
              if (typeof val === 'string') setQuery(val);
              else if (val && typeof val.name === 'string') setQuery(val.name);
              else setQuery('');
            }}
            completeMethod={search}
            suggestions={suggestions}
            field="name"
            itemTemplate={itemTemplate}
            placeholder={t('icon.search')}
            className="icon-picker-search"
            onSelect={(e) => {
              commit(`pi pi-${e.value.name}`);
              setQuery('');
            }}
          />

          <div className="icon-picker-meta">
            <span>{filteredIcons.length} icons</span>
            {value && (
              <span className="icon-picker-current">
                <i className={value} />
                <span>{value.replace('pi pi-', '')}</span>
              </span>
            )}
          </div>
        </div>

        <div className="icon-grid">
          {filteredIcons.map((icon) => {
              const iconClass = `pi pi-${icon.name}`;
              return (
                <button
                  type="button"
                  key={icon.name}
                  className={`icon-button ${value === iconClass ? 'selected' : ''}`}
                  onClick={() => commit(iconClass)}
                >
                  <i className={iconClass} />
                </button>
              );
            })}
          {!filteredIcons.length && (
            <div className="icon-picker-empty">{t('datatable.filter.empty-table', { type: 'icons' })}</div>
          )}
        </div>
      </AppDialog>
    </>
  );
};

export default IconPicker;
